/**
 * Shared rarity frame for procedural item icons — World Art Pack 2, Task 3.
 *
 * Wraps a piece of inner SVG markup (drawn in a 24x24 coordinate space) in
 * the canonical 32-viewBox item-icon frame: a rarity-colored ring inset by
 * 1px, plus (for the top rarity tier) a soft outer halo using the same hue.
 * This is the ONE place framing is drawn — Tasks 4-6's kind generators only
 * ever produce `inner` markup and hand it to `framedIcon`.
 *
 * Colors come from `itemkit/shared.ts` (`rarityColor`/`rarityGlow`), which
 * is itself a re-export of the canonical `RARITY`/`RARITY_GLOW` theme
 * palette — never duplicate that hex map here.
 *
 * Naming note: `ItemRarity` (src/shared/CharacterTypes.ts) declares its top
 * tier as `'unique'`, but the real ITEM_DATABASE content never uses that
 * string — it uses `'legendary'` (9 items; already `as ItemRarity`-cast in
 * the generated content, which the type checker doesn't reject because
 * both are string-literal subtypes -- see the report for the full note).
 * `rarityColor`/`rarityGlow` key off the theme's `RARITY` map, which DOES
 * define `'legendary'` (not `'unique'`). So the halo gate below checks for
 * either spelling — whichever ships, the mythic tier gets the extra glow.
 */
import { rarityColor, rarityGlow } from '@/game/ui/components/itemkit/shared';
import { svgToBase64 } from '@/game/rendering/svg/SVGUtils';
import type { ItemRarity } from '@/shared/CharacterTypes';

const DEFAULT_RING_COLOR = 'rgba(255, 255, 255, 0.2)';

/** Top-tier rarities that additionally get the outer halo. See naming note above. */
function isTopTier(rarity: ItemRarity): boolean {
  return rarity === 'unique' || (rarity as string) === 'legendary';
}

/**
 * Wrap `inner` (24x24 art) in the 32-viewBox rarity frame and return a
 * base64 `data:image/svg+xml` URL — same convention as
 * `getItemIconSVG`/`svgToBase64` elsewhere in this registry.
 */
export function framedIcon(inner: string, rarity: ItemRarity, size: number): string {
  const ringColor = rarityColor(rarity) ?? DEFAULT_RING_COLOR;
  const glow = rarityGlow(rarity);
  const hasHalo = isTopTier(rarity) && Boolean(glow) && glow !== 'none';

  const haloDefs = hasHalo
    ? `<defs><filter id="rarityHalo" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="1.6" result="blur"/>
      </filter></defs>`
    : '';
  const halo = hasHalo
    ? `<circle cx="16" cy="16" r="14.5" fill="none" stroke="${ringColor}" stroke-width="2" opacity="0.55" filter="url(#rarityHalo)"/>`
    : '';

  const svg = `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    ${haloDefs}${halo}
    <g transform="translate(4,4)">${inner}</g>
    <rect x="1" y="1" width="30" height="30" rx="4" fill="none" stroke="${ringColor}" stroke-width="1.5"/>
  </svg>`;

  return svgToBase64(svg);
}
